import React, { FormEvent } from 'react';
import type { AdminSummary } from '../../../../api/adminApi';
import { Eyebrow, PageHeading } from '../../../../design-system';
import styles from './ProfileSection.module.css';

export interface ProfileMeta {
  profileDisplayName: string;
  profileInitial: string;
  profileEmail: string;
  profileJoinLabel: string;
  welcomeLabel: string;
}

export interface ProfileSectionProps {
  profileMeta: ProfileMeta;
  editingProfileName: boolean;
  profileNameInput: string;
  profileUpdateError: string | null;
  profileUpdateLoading: boolean;
  userPreviewActive: boolean;
  currentAdmin?: AdminSummary | null;
  onNameInputChange: (value: string) => void;
  onProfileEditStart: () => void;
  onProfileEditCancel: () => void;
  onProfileSave: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  onSwitchAdminView: () => void;
  onSwitchUserView: () => void;
}

export const ProfileSection: React.FC<ProfileSectionProps> = ({
  profileMeta,
  editingProfileName,
  profileNameInput,
  profileUpdateError,
  profileUpdateLoading,
  userPreviewActive,
  currentAdmin,
  onNameInputChange,
  onProfileEditStart,
  onProfileEditCancel,
  onProfileSave,
  onSwitchAdminView,
  onSwitchUserView,
}) => {
  const isDefault = !currentAdmin?.custom_role_id;
  const pages = currentAdmin?.allowed_pages ?? [];
  const features = currentAdmin?.allowed_features ?? [];
  const actions = currentAdmin?.allowed_actions ?? [];

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <Eyebrow tone="default" dot>PROFILE</Eyebrow>
        <PageHeading
          accentWord="profile"
          accentTone="orange"
          size="h2"
          align="left"
          level="h2"
        >
          Your admin profile
        </PageHeading>
      </div>
      <div className={styles.detailCard}>
        <div className={styles.detailContent}>
          <div className={styles.profileDetailHeader}>
            <div className={styles.profileAvatar}>{profileMeta.profileInitial}</div>
            <div>
              <p className={styles.profileName}>{profileMeta.profileDisplayName}</p>
            </div>
          </div>
          <div className={styles.profileDetailBody}>
            <div className={styles.profileAttribute}>
              <span className={styles.profileAttributeLabel}>Full name</span>
              {editingProfileName ? (
                <form className={styles.profileEditForm} onSubmit={onProfileSave}>
                  <input
                    className={styles.profileInput}
                    type="text"
                    value={profileNameInput}
                    onChange={(e) => onNameInputChange(e.target.value)}
                  />
                  {profileUpdateError && <p className={styles.errorText}>{profileUpdateError}</p>}
                  <div className={styles.profileEditActions}>
                    <button type="submit" className={styles.profileSaveButton} disabled={profileUpdateLoading}>
                      {profileUpdateLoading ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      className={styles.profileCancelButton}
                      onClick={onProfileEditCancel}
                      disabled={profileUpdateLoading}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <p className={styles.profileAttributeValue}>{profileMeta.profileDisplayName}</p>
              )}
            </div>
            <div className={styles.profileAttribute}>
              <span className={styles.profileAttributeLabel}>Email</span>
              <p className={styles.profileAttributeValue}>{profileMeta.profileEmail}</p>
            </div>
            <div className={styles.profileAttribute}>
              <span className={styles.profileAttributeLabel}>Active since</span>
              <p className={styles.profileAttributeValue}>{profileMeta.profileJoinLabel}</p>
            </div>
          </div>
          {currentAdmin && (
            <div className={styles.permissionsCard}>
              <div className={styles.permissionsHeader}>
                <span className={styles.permissionsTitle}>My permissions</span>
                <span className={isDefault ? styles.badgeFull : styles.badgeCustom}>
                  {isDefault ? 'Full access' : (currentAdmin.role_name ?? 'Custom role')}
                </span>
              </div>
              {isDefault ? (
                <p className={styles.permissionsNote}>You have full default Admin access — all pages and actions are available.</p>
              ) : (
                <div className={styles.permissionsGroups}>
                  {([
                    { label: 'Pages', items: pages },
                    { label: 'Features', items: features },
                    { label: 'Actions', items: actions },
                  ] as const).map(({ label, items }) => (
                    <div key={label} className={styles.permGroup}>
                      <span className={styles.permGroupLabel}>{label}</span>
                      <div className={styles.chipRow}>
                        {items.length > 0
                          ? items.map((item) => (
                              <span key={item} className={styles.chip}>{item.replace(/_/g, ' ')}</span>
                            ))
                          : <span className={styles.permNone}>None</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Removed user preview access (User view) from admin profile section */}
          {!editingProfileName && (
            <button
              type="button"
              className={`${styles.button} ${styles.profileButton}`}
              onClick={onProfileEditStart}
            >
              Update profile
            </button>
          )}
        </div>
      </div>
    </section>
  );
};
